import * as React from 'react';
import isEmpty from 'lodash/isEmpty';

import { Form, FormField } from 'components/form';
import ActionButton from 'components/buttons/action-button/ActionButton';
import ActionLink from 'components/buttons/action-link/ActionLink';
import Input from 'components/inputs/input/Input';
import { ModalFrameFooter, ModalFrameInner, ModalFrameMessage } from 'components/modal';

import { t } from 'utils/language';
import { isEmail, isNotEmpty, isRequired } from 'utils/validators';
import { shouldInfluencerFillBusinessDetails } from 'utils/organization';

import './shipping-step-details-form.scss';

import type { ActionButtonColor, OrganizationSettingsForm, ShippingAddress } from 'types';
import type { ChangeEvent } from 'react';

type Props = {
  allowDelete: boolean,
  organizationSettings: OrganizationSettingsForm,
  submitButtonColor?: ActionButtonColor,
  isEditCampaignAddress?: boolean,
  cancelButtonLabel?: string,
  title?: string,
  address?: ShippingAddress | null,
  onSave: (a?: ShippingAddress | null) => void,
  onCancel: () => void,
  onDelete?: (a?: ShippingAddress | null) => void
};

type State = {
  id?: number,
  recipient: string,
  address1: string,
  address2: string,
  city: string,
  stateProvinceRegion: string,
  zip: string,
  country: string,
  email?: string
};

class ShippingStepDetailsForm extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    const { address } = props;

    if (address) {
      this.state = {
        recipient: address.recipient,
        address1: address.address1,
        address2: address.address2,
        email: address.email,
        city: address.city,
        stateProvinceRegion: address.stateProvinceRegion,
        zip: address.zip,
        country: address.country,
        id: address.id
      };
    } else {
      this.state = {
        recipient: '',
        address1: '',
        address2: '',
        city: '',
        stateProvinceRegion: '',
        zip: '',
        country: ''
      };
    }
  }

  handleDelete = (event: ChangeEvent<HTMLInputElement>): void => {
    event.preventDefault();

    const { onDelete, address } = this.props;

    if (onDelete) {
      onDelete(address);
    }
  };

  handlePopulateFields = () => {
    const { billingAddress, businessDetails } = this.props.organizationSettings;

    this.setState({
      recipient: businessDetails.companyName,
      address1: billingAddress.addressLine1,
      city: billingAddress.city,
      stateProvinceRegion: billingAddress.state,
      zip: billingAddress.postalCode,
      country: billingAddress.country
    });
  };

  handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const input = event.target;
    const name = input.name as keyof Omit<State, 'id'>;

    // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/26635
    this.setState<any>({
      [name]: input.value
    });
  };

  handleSave = () => {
    const { onSave } = this.props;

    onSave(this.state);
  };

  render(): React.ReactNode {
    const {
      address,
      allowDelete,
      title,
      organizationSettings,
      submitButtonColor,
      isEditCampaignAddress,
      cancelButtonLabel,
      onCancel } = this.props;
    const {
      recipient,
      address1,
      address2,
      city,
      stateProvinceRegion,
      zip,
      country,
      email
    } = this.state;

    const message = address ? t('shipping_address.address_details_edit_title') : t('shipping_address.address_details_create_title');

    return (
      <ModalFrameInner
        narrowContent
      >
        <ModalFrameMessage
          message={title || message}
        />
        <div className="shipping-step-details-form__form">
          {
            !shouldInfluencerFillBusinessDetails(organizationSettings) && isEmpty(address) &&
              <div
                onClick={this.handlePopulateFields}
                className="shipping-step-details-form__populate-link"
              >
                {t('shipping_address.populate_fields')}
              </div>
          }
          <Form onSubmit={this.handleSave}>
            <FormField
              label={t('shipping_address.address_details_recipient_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isNotEmpty(recipient),
                isRequired(recipient)
              ]}
            >
              <Input
                type="text"
                name="recipient"
                width="responsive"
                value={recipient ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            {
              isEditCampaignAddress && (
                <FormField
                  label={t('shipping_address.label_email_address')}
                  labelType="highlighted"
                  direction="column"
                  fontSize="small"
                  showRequiredMark
                  showValidationIcon={false}
                  validators={[
                    isRequired(email),
                    isEmail(email)
                  ]}
                >
                  <Input
                    type="text"
                    name="email"
                    width="responsive"
                    value={email ?? ''}
                    onChange={this.handleInputChange}
                  />
                </FormField>
              )
            }
            <FormField
              label={t('shipping_address.address_details_address_1_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              showValidationIcon={false}
              validators={[
                isNotEmpty(address1),
                isRequired(address1)
              ]}
            >
              <Input
                type="text"
                name="address1"
                width="responsive"
                value={address1 ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            <FormField
              label={t('shipping_address.address_details_address_2_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showValidationIcon={false}
            >
              <Input
                type="text"
                name="address2"
                width="responsive"
                value={address2 ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            <FormField
              showValidationIcon={false}
              label={t('shipping_address.address_details_city_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              validators={[
                isNotEmpty(city),
                isRequired(city)
              ]}
            >
              <Input
                type="text"
                name="city"
                width="responsive"
                value={city ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            <FormField
              label={t('shipping_address.address_details_state_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showValidationIcon={false}
            >
              <Input
                type="text"
                name="stateProvinceRegion"
                width="responsive"
                value={stateProvinceRegion ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            <FormField
              showValidationIcon={false}
              label={t('shipping_address.address_details_zip_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              validators={[
                isNotEmpty(zip),
                isRequired(zip)
              ]}
            >
              <Input
                type="text"
                name="zip"
                width="responsive"
                value={zip ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            <FormField
              showValidationIcon={false}
              label={t('shipping_address.address_details_country_label')}
              labelType="highlighted"
              direction="column"
              fontSize="small"
              showRequiredMark
              validators={[
                isNotEmpty(country),
                isRequired(country)
              ]}
            >
              <Input
                type="text"
                name="country"
                width="responsive"
                value={country ?? ''}
                onChange={this.handleInputChange}
              />
            </FormField>
            {
              allowDelete &&
                <div className="shipping-step-details-form__link-wrapper">
                  <ActionLink
                    color="danger"
                    onClick={this.handleDelete}
                  >
                    {t('shipping_address.label_delete_address')}
                  </ActionLink>
                </div>
            }
            <ModalFrameFooter>
              <ActionButton
                color="tertiary"
                onClick={onCancel}
              >
                {cancelButtonLabel}
              </ActionButton>
              <ActionButton
                type="submit"
                color={submitButtonColor}
              >
                {t('shipping_address.button_save_changes')}
              </ActionButton>
            </ModalFrameFooter>
          </Form>
        </div>
      </ModalFrameInner>
    );
  }
}

export default ShippingStepDetailsForm;
